import { useEffect, useRef, useState } from "react";
import { presentationFor } from "./niche-presentation.js";
import "./access-demo.css";


function ClinicLogo() {
  return <svg viewBox="0 0 40 40" role="img" aria-label="Logotipo original da Clínica Bem Sorrir, clínica fictícia"><rect width="40" height="40" rx="13" fill="#e2f5f1"/><path d="M12 10c3-2 5 0 8 0s5-2 8 0c5 4 0 17-3 20-2 2-2-8-5-8s-3 10-5 8c-3-3-8-16-3-20Z" fill="none" stroke="#207e80" strokeWidth="1.7" strokeLinejoin="round"/><path d="M13 16c4 5 10 5 14 0" fill="none" stroke="#207e80" strokeWidth="1.7" strokeLinecap="round"/></svg>;
}

export function AccessDemo({ experienceKey }: { experienceKey?: string }) {
  const { demo } = presentationFor(experienceKey);
  const messages = demo.messages;
  const cycle = demo.tone === "clinic" ? 74000 : 30000;
  const root = useRef<HTMLElement>(null);
  const timeline = useRef<HTMLDivElement>(null);
  const elapsed = useRef(0);
  const [time, setTime] = useState(0);
  const [visible, setVisible] = useState(false);
  const [pageVisible, setPageVisible] = useState(!document.hidden);
  const [reduced, setReduced] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [paused, setPaused] = useState(false);
  const active = visible && pageVisible && !reduced && !paused;
  const count = reduced ? messages.length : messages.filter(message => time >= message.at).length;
  const typing = !reduced && messages.some((message) => message.sender === "assistant" && time >= message.at - 1500 && time < message.at);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setVisible(Boolean(entry?.isIntersecting)), { threshold: 0.2 });
    if (root.current) observer.observe(root.current);
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const motion = () => setReduced(media.matches);
    const visibility = () => setPageVisible(!document.hidden);
    media.addEventListener("change", motion); document.addEventListener("visibilitychange", visibility);
    return () => { observer.disconnect(); media.removeEventListener("change", motion); document.removeEventListener("visibilitychange", visibility); };
  }, []);

  useEffect(() => {
    if (!active) return;
    let previous = performance.now();
    const timer = window.setInterval(() => {
      const now = performance.now(); elapsed.current += now - previous; previous = now;
      if (elapsed.current >= cycle) elapsed.current = 0;
      setTime(elapsed.current);
    }, 150);
    return () => window.clearInterval(timer);
  }, [active]);

  useEffect(() => {
    // Only the phone scrolls. Keep the page and form at their current positions.
    const frame = requestAnimationFrame(() => {
      const pane = timeline.current;
      if (pane) pane.scrollTo({ top: count === 0 ? 0 : pane.scrollHeight, behavior: reduced || count === 0 ? "instant" : "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [count, typing, reduced]);

  function replay() {
    elapsed.current = 0; setTime(0); setPaused(false);
    timeline.current?.scrollTo({ top: 0, behavior: "instant" });
  }

  return <section ref={root} className="access-demo access-demo-phone" data-demo={demo.tone} data-count={count} data-running={active} aria-label={`Demonstração ilustrativa fictícia de ${demo.company}`}>
    <div className="bem-phone"><div className="bem-screen">
      <div className="bem-status" aria-hidden="true"><span>14:32</span><span className="bem-island"/><span className="bem-indicators"><svg viewBox="0 0 18 12"><path d="M1 11V8m4 3V6m4 5V3m4 8V0" stroke="currentColor" strokeWidth="2"/></svg><svg viewBox="0 0 21 12"><rect x="1" y="1" width="16" height="10" rx="2" fill="none" stroke="currentColor"/><path d="M19 4v4" stroke="currentColor" strokeWidth="2"/><rect x="3" y="3" width="12" height="6" rx="1" fill="currentColor"/></svg></span></div>
      <div className="bem-head"><span className="bem-back" aria-hidden="true">‹</span><span className="bem-logo">{demo.tone === "clinic" ? <ClinicLogo/> : <span className="bem-letter-logo">{demo.initials}</span>}</span><div><strong>{demo.company}</strong><small>{typing ? "Digitando…" : demo.subtitle}</small></div><span className="bem-menu" aria-hidden="true">⋮</span></div>
      <div ref={timeline} className="bem-timeline" tabIndex={0} aria-label="Conversa fictícia, histórico da demonstração" aria-live="off">
        <div className="bem-day">HOJE · DEMONSTRAÇÃO</div>
        {count === 0 && <p className="bem-wait">Uma nova conversa começa aqui.</p>}
        {messages.slice(0,count).map((message,index) => {
          return <article key={index} className={`bem-bubble ${message.sender}`} data-message={index+1}>
            <p>{message.text}</p>
            <time>14:{index<4?"32":"33"}{message.sender === "assistant" && <span aria-hidden="true"> ✓✓</span>}</time>
          </article>;
        })}
        {typing && <div className="bem-typing" aria-label="G3.AI digitando"><i/><i/><i/></div>}
      </div>
      <div className="bem-compose" aria-hidden="true"><span>＋</span><div>Mensagem <span>☺</span></div><svg viewBox="0 0 24 24"><path d="m4 4 17 8-17 8 3-8-3-8Z" fill="currentColor"/></svg></div>
      <div className="bem-home" aria-hidden="true"><span/></div>
    </div></div>
    <footer className="bem-demo-footer"><span>Demonstração fictícia · não representa uma conversa real</span><div className="bem-demo-actions">{reduced ? <span>Movimento reduzido</span> : <><button type="button" onClick={() => setPaused(value=>!value)} aria-label={paused ? "Continuar demonstração" : "Pausar demonstração"}>{paused ? "Continuar" : "Pausar"}</button><button type="button" onClick={replay}>Rever</button></>}</div></footer>
  </section>;
}
